import { HeadStyle } from 'modules/secureIframe/HeadStyle';
import { BUTTON_VARIANTS } from 'ui/button';
import { SegmentedControl } from 'ui/segmentedControl';
import { BasicSelect } from 'ui/selects';
import { AnimatedRefreshIcon, Desktop16, InfoFill, PlatformMobile16 } from 'ui/icons';
import { useLocalStorage } from 'utils/hooks/useLocalStorage';

import {
  PreviewContent,
  PreviewControls,
  PreviewFooter,
  PreviewMessage,
  PreviewRefreshButton,
} from '../../common';
import { Preview } from './Preview';
import { WebPushContentDefinition } from '../../../graphql/types';
import { useEditableFieldsContext } from '../../context/EditableFieldsContext';
import { useWebPushPreviewContext } from '../context/WebPushPreviewContext';
import { OSWebPush, PlatformWebPush } from '../../common/types';
import { displayContent } from '../../common/content';
import { hasHandlebars } from '../../common/utils';
import { PreviewHeader } from '../../common/PreviewHeader';
import {
  HELP_MESSAGE,
  PREVIEW_REFRESH_BUTTON,
  PREVIEWER_CONTENT,
  TEMPLATE_EDITOR_WEB_PUSH_BODY,
  TEMPLATE_EDITOR_WEB_PUSH_PLATFORM_SELECTOR,
  TEMPLATE_EDITOR_WEB_PUSH_PREVIEW_PANE_ID,
  TEMPLATE_EDITOR_WEB_PUSH_TITLE,
  TEMPLATE_EDITOR_WEB_PUSH_PREVIEW_FOOTER,
} from '../constants';
import { WebPushPreviewContainer } from './styles';

export const PreviewPane = () => {
  const {
    isLoading: isRefreshing,
    messageContentTitle,
    messageContentBody,
    onRefreshPreview,
    isPreviewWithData,
  } = useWebPushPreviewContext();
  const { liveTemplate, hasContent } = useEditableFieldsContext();
  const [selectedPlatform, setSelectedPlatform] = useLocalStorage<PlatformWebPush>(
    'webPushPreviewPlatform',
    PlatformWebPush.Desktop,
  );
  const [selectedOS, setSelectedOS] = useLocalStorage<OSWebPush>(
    'webPushPreviewOS',
    OSWebPush.MacOS,
  );
  const webPushData = (liveTemplate?.mediumData || {}) as WebPushContentDefinition;
  const hasDataParamsInMsg =
    !isPreviewWithData && hasHandlebars(webPushData?.title) && hasHandlebars(webPushData?.message);
  const title = isPreviewWithData ? messageContentTitle : webPushData.title;
  const body = isPreviewWithData ? messageContentBody : webPushData.message;

  const previewPlatformOptions = [
    { Icon: Desktop16, value: PlatformWebPush.Desktop, disabled: !hasContent },
    { Icon: PlatformMobile16, value: PlatformWebPush.Mobile, disabled: !hasContent },
  ];

  const previewOsOptions =
    selectedPlatform === PlatformWebPush.Desktop
      ? [
          { id: OSWebPush.MacOS, label: OSWebPush.MacOS, value: OSWebPush.MacOS },
          { id: OSWebPush.Windows, label: OSWebPush.Windows, value: OSWebPush.Windows },
        ]
      : [
          { id: OSWebPush.iOS, label: OSWebPush.iOS, value: OSWebPush.iOS },
          { id: OSWebPush.Android, label: OSWebPush.Android, value: OSWebPush.Android },
        ];

  return (
    <WebPushPreviewContainer
      data-test={TEMPLATE_EDITOR_WEB_PUSH_PREVIEW_PANE_ID}
      isPreviewWithData={isPreviewWithData}
    >
      <PreviewHeader />
      <PreviewContent
        HeadContent={<HeadStyle />}
        $height={selectedPlatform === PlatformWebPush.Desktop || !hasContent ? '510px' : '600px'}
      >
        <Preview selectedPlatform={selectedPlatform} selectedOS={selectedOS} />
      </PreviewContent>
      {isPreviewWithData && (
        <PreviewRefreshButton
          onClick={() => onRefreshPreview()}
          variant={BUTTON_VARIANTS.SECONDARY}
          data-test={PREVIEW_REFRESH_BUTTON}
        >
          <AnimatedRefreshIcon $isAnimating={isRefreshing} />
        </PreviewRefreshButton>
      )}
      <PreviewFooter data-test={TEMPLATE_EDITOR_WEB_PUSH_PREVIEW_FOOTER}>
        <PreviewControls>
          <SegmentedControl
            options={previewPlatformOptions}
            onSelect={(platform) => {
              setSelectedPlatform(platform);
              setSelectedOS(platform === PlatformWebPush.Desktop ? OSWebPush.MacOS : OSWebPush.iOS);
            }}
            data-test={TEMPLATE_EDITOR_WEB_PUSH_PLATFORM_SELECTOR}
            value={selectedPlatform}
          />
          <BasicSelect
            options={previewOsOptions}
            onSelect={([selected]) => setSelectedOS(selected)}
            defaultValues={[selectedOS]}
            isDisabled={!hasContent}
          />
        </PreviewControls>
        <PreviewMessage>
          {hasDataParamsInMsg ? (
            <>
              <InfoFill height={12} width={12} isColorInherited />
              <span data-test={HELP_MESSAGE}>{PREVIEWER_CONTENT.helpMessage}</span>
            </>
          ) : (
            <>
              <span>Title: </span>
              <span data-test={TEMPLATE_EDITOR_WEB_PUSH_TITLE}>
                {displayContent.strCharacterLength({ charCount: title?.length || 0 })}
              </span>
              <span>|</span>
              <span>Body: </span>
              <span data-test={TEMPLATE_EDITOR_WEB_PUSH_BODY}>
                {displayContent.strCharacterLength({ charCount: body?.length || 0 })}
              </span>
            </>
          )}
        </PreviewMessage>
      </PreviewFooter>
    </WebPushPreviewContainer>
  );
};

export default PreviewPane;
